import { useState } from 'react'
import { Copy, Check } from 'lucide-react'

const languages = [
  { id: 'curl', name: 'cURL' },
  { id: 'javascript', name: 'JavaScript' },
  { id: 'python', name: 'Python' },
  { id: 'php', name: 'PHP' },
  { id: 'ruby', name: 'Ruby' },
  { id: 'go', name: 'Go' },
  { id: 'java', name: 'Java' },
  { id: 'csharp', name: 'C#' },
]

const codeExamples = {
  curl: `curl https://api.textredge.com/api/v1/sms/send \\
  -X POST \\
  -H "X-API-Key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "to": "+1234567890",
    "message": "Hello from redgeGate!"
  }'`,
  javascript: `const axios = require('axios');

const apiKey = 'your_api_key_here';
const baseURL = 'https://api.textredge.com/api/v1';

async function sendSMS(to, message) {
  try {
    const response = await axios.post(\`\${baseURL}/sms/send\`, {
      to,
      message
    }, {
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Error:', error.response?.data || error.message);
    throw error;
  }
}

// Usage
sendSMS('+1234567890', 'Hello from JavaScript!')
  .then(result => console.log(result))
  .catch(err => console.error(err));`,
  python: `import requests

API_KEY = 'your_api_key_here'
BASE_URL = 'https://api.textredge.com/api/v1'

def send_sms(to, message):
    headers = {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json'
    }

    payload = {
        'to': to,
        'message': message
    }

    try:
        response = requests.post(
            f'{BASE_URL}/sms/send',
            json=payload,
            headers=headers
        )
        response.raise_for_status()
        return response.json()
    except requests.exceptions.RequestException as e:
        print(f'Error: {e}')
        raise

# Usage
result = send_sms('+1234567890', 'Hello from Python!')
print(result)`,
  php: `<?php

$apiKey = 'your_api_key_here';
$baseURL = 'https://api.textredge.com/api/v1';

function sendSMS($to, $message) {
    global $apiKey, $baseURL;

    $data = [
        'to' => $to,
        'message' => $message
    ];

    $ch = curl_init("$baseURL/sms/send");
    curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
    curl_setopt($ch, CURLOPT_POST, true);
    curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
    curl_setopt($ch, CURLOPT_HTTPHEADER, [
        'X-API-Key: ' . $apiKey,
        'Content-Type: application/json'
    ]);

    $response = curl_exec($ch);

    if (curl_errno($ch)) {
        throw new Exception('Error: ' . curl_error($ch));
    }

    curl_close($ch);
    return json_decode($response, true);
}

// Usage
$result = sendSMS('+1234567890', 'Hello from PHP!');
print_r($result);`,
  ruby: `require 'net/http'
require 'json'
require 'uri'

API_KEY = 'your_api_key_here'
BASE_URL = 'https://api.textredge.com/api/v1'

def send_sms(to, message)
  uri = URI("#{BASE_URL}/sms/send")

  http = Net::HTTP.new(uri.host, uri.port)
  http.use_ssl = true

  request = Net::HTTP::Post.new(uri)
  request['X-API-Key'] = API_KEY
  request['Content-Type'] = 'application/json'
  request.body = {
    to: to,
    message: message
  }.to_json

  response = http.request(request)
  JSON.parse(response.body)
rescue StandardError => e
  puts "Error: #{e.message}"
  nil
end

# Usage
result = send_sms('+1234567890', 'Hello from Ruby!')
puts result`,
  go: `package main

import (
    "bytes"
    "encoding/json"
    "fmt"
    "io"
    "net/http"
)

const (
    APIKey  = "your_api_key_here"
    BaseURL = "https://api.textredge.com/api/v1"
)

type SMSRequest struct {
    To      string \`json:"to"\`
    Message string \`json:"message"\`
}

func sendSMS(to, message string) error {
    reqBody := SMSRequest{
        To:      to,
        Message: message,
    }

    jsonData, err := json.Marshal(reqBody)
    if err != nil {
        return err
    }

    req, err := http.NewRequest("POST", BaseURL+"/sms/send", bytes.NewBuffer(jsonData))
    if err != nil {
        return err
    }

    req.Header.Set("X-API-Key", APIKey)
    req.Header.Set("Content-Type", "application/json")

    client := &http.Client{}
    resp, err := client.Do(req)
    if err != nil {
        return err
    }
    defer resp.Body.Close()

    body, _ := io.ReadAll(resp.Body)
    fmt.Println(string(body))
    return nil
}

func main() {
    sendSMS("+1234567890", "Hello from Go!")
}`,
  java: `import java.net.URI;
import java.net.http.HttpClient;
import java.net.http.HttpRequest;
import java.net.http.HttpResponse;
import org.json.JSONObject;

public class TextRedgeClient {
    private static final String API_KEY = "your_api_key_here";
    private static final String BASE_URL = "https://api.textredge.com/api/v1";

    public static void sendSMS(String to, String message) throws Exception {
        HttpClient client = HttpClient.newHttpClient();

        JSONObject requestBody = new JSONObject();
        requestBody.put("to", to);
        requestBody.put("message", message);

        HttpRequest request = HttpRequest.newBuilder()
            .uri(URI.create(BASE_URL + "/sms/send"))
            .header("X-API-Key", API_KEY)
            .header("Content-Type", "application/json")
            .POST(HttpRequest.BodyPublishers.ofString(requestBody.toString()))
            .build();

        HttpResponse<String> response = client.send(request,
            HttpResponse.BodyHandlers.ofString());

        System.out.println(response.body());
    }

    public static void main(String[] args) {
        try {
            sendSMS("+1234567890", "Hello from Java!");
        } catch (Exception e) {
            e.printStackTrace();
        }
    }
}`,
  csharp: `using System;
using System.Net.Http;
using System.Text;
using System.Text.Json;
using System.Threading.Tasks;

namespace TextRedgeClient
{
    public class SMSClient
    {
        private const string ApiKey = "your_api_key_here";
        private const string BaseUrl = "https://api.textredge.com/api/v1";
        private readonly HttpClient _httpClient;

        public SMSClient()
        {
            _httpClient = new HttpClient();
            _httpClient.DefaultRequestHeaders.Add("X-API-Key", ApiKey);
        }

        public async Task<string> SendSMS(string to, string message)
        {
            var requestBody = new
            {
                to = to,
                message = message
            };

            var json = JsonSerializer.Serialize(requestBody);
            var content = new StringContent(json, Encoding.UTF8, "application/json");

            var response = await _httpClient.PostAsync($"{BaseUrl}/sms/send", content);
            response.EnsureSuccessStatusCode();

            return await response.Content.ReadAsStringAsync();
        }
    }

    class Program
    {
        static async Task Main(string[] args)
        {
            var client = new SMSClient();
            var result = await client.SendSMS("+1234567890", "Hello from C#!");
            Console.WriteLine(result);
        }
    }
}`
}

export default function SendSMS() {
  const [selectedLang, setSelectedLang] = useState('curl')
  const [copied, setCopied] = useState(false)

  const copyCode = () => {
    navigator.clipboard.writeText(codeExamples[selectedLang as keyof typeof codeExamples])
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="prose prose-blue max-w-none">
      <h1>Send SMS</h1>

      <p className="text-xl text-gray-600">
        Send a single SMS message to a phone number.
      </p>

      <div className="not-prose bg-blue-50 border-l-4 border-blue-600 p-4 my-6">
        <p className="text-sm text-blue-900">
          <strong>Endpoint:</strong> <code className="bg-blue-100 px-2 py-1 rounded">POST /api/v1/sms/send</code>
        </p>
      </div>

      <h2>Request</h2>

      <h3>Headers</h3>
      <div className="not-prose overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Header</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Required</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-4 text-sm font-mono">X-API-Key</td>
              <td className="px-6 py-4 text-sm text-gray-600">Your API key</td>
              <td className="px-6 py-4 text-sm"><span className="text-red-600">Required</span></td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-mono">Content-Type</td>
              <td className="px-6 py-4 text-sm text-gray-600">application/json</td>
              <td className="px-6 py-4 text-sm"><span className="text-red-600">Required</span></td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Body Parameters</h3>
      <div className="not-prose overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-4 text-sm font-mono">to</td>
              <td className="px-6 py-4 text-sm text-gray-600">string</td>
              <td className="px-6 py-4 text-sm text-gray-600">Recipient phone number in E.164 format (e.g., +1234567890)</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-mono">message</td>
              <td className="px-6 py-4 text-sm text-gray-600">string</td>
              <td className="px-6 py-4 text-sm text-gray-600">Message content (max 1600 characters)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Response</h2>

      <h3>Success Response (200 OK)</h3>
      <div className="not-prose bg-gray-900 text-gray-100 rounded-lg p-4 my-4">
        <pre className="text-sm overflow-x-auto">
{`{
  "success": true,
  "data": {
    "smsId": "msg_1a2b3c4d5e6f",
    "status": "queued",
    "recipient": "+1234567890",
    "message": "Hello from redgeGate!",
    "createdAt": "2024-01-15T10:30:00.000Z"
  }
}`}
        </pre>
      </div>

      <h3>Response Schema</h3>
      <div className="not-prose overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Field</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <tr>
              <td className="px-6 py-4 text-sm font-mono">success</td>
              <td className="px-6 py-4 text-sm text-gray-600">boolean</td>
              <td className="px-6 py-4 text-sm text-gray-600">Indicates if the request was successful</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-mono">data.smsId</td>
              <td className="px-6 py-4 text-sm text-gray-600">string</td>
              <td className="px-6 py-4 text-sm text-gray-600">Unique identifier for the SMS message</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-mono">data.status</td>
              <td className="px-6 py-4 text-sm text-gray-600">string</td>
              <td className="px-6 py-4 text-sm text-gray-600">Message status: queued, sent, delivered, failed</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-mono">data.recipient</td>
              <td className="px-6 py-4 text-sm text-gray-600">string</td>
              <td className="px-6 py-4 text-sm text-gray-600">Recipient phone number</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-mono">data.message</td>
              <td className="px-6 py-4 text-sm text-gray-600">string</td>
              <td className="px-6 py-4 text-sm text-gray-600">Message content</td>
            </tr>
            <tr>
              <td className="px-6 py-4 text-sm font-mono">data.createdAt</td>
              <td className="px-6 py-4 text-sm text-gray-600">string</td>
              <td className="px-6 py-4 text-sm text-gray-600">ISO 8601 timestamp of message creation</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Error Responses</h3>
      <div className="not-prose space-y-4 my-4">
        <div>
          <p className="font-semibold text-sm mb-2">400 Bad Request - Invalid phone number</p>
          <div className="bg-gray-900 text-gray-100 rounded-lg p-4">
            <pre className="text-sm overflow-x-auto">
{`{
  "success": false,
  "error": {
    "code": "INVALID_PHONE_NUMBER",
    "message": "Invalid phone number format. Use E.164 format.",
    "statusCode": 400
  }
}`}
            </pre>
          </div>
        </div>

        <div>
          <p className="font-semibold text-sm mb-2">401 Unauthorized - Invalid API key</p>
          <div className="bg-gray-900 text-gray-100 rounded-lg p-4">
            <pre className="text-sm overflow-x-auto">
{`{
  "success": false,
  "error": {
    "code": "INVALID_API_KEY",
    "message": "Invalid or missing API key",
    "statusCode": 401
  }
}`}
            </pre>
          </div>
        </div>

        <div>
          <p className="font-semibold text-sm mb-2">429 Too Many Requests - Rate limit exceeded</p>
          <div className="bg-gray-900 text-gray-100 rounded-lg p-4">
            <pre className="text-sm overflow-x-auto">
{`{
  "success": false,
  "error": {
    "code": "RATE_LIMIT_EXCEEDED",
    "message": "Rate limit exceeded. Try again in 3600 seconds.",
    "statusCode": 429,
    "retryAfter": 3600
  }
}`}
            </pre>
          </div>
        </div>
      </div>

      <h2>Code Examples</h2>

      <div className="not-prose">
        <div className="flex gap-2 border-b border-gray-200 mb-4 overflow-x-auto">
          {languages.map(lang => (
            <button
              key={lang.id}
              onClick={() => setSelectedLang(lang.id)}
              className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
                selectedLang === lang.id
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {lang.name}
            </button>
          ))}
        </div>

        <div className="relative">
          <button
            onClick={copyCode}
            className="absolute top-3 right-3 p-2 bg-gray-800 hover:bg-gray-700 rounded text-white transition-colors"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </button>
          <div className="bg-gray-900 text-gray-100 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm">
              {codeExamples[selectedLang as keyof typeof codeExamples]}
            </pre>
          </div>
        </div>
      </div>

      <h2>Status Lifecycle</h2>
      <p>Messages go through the following status transitions:</p>
      <ol>
        <li><code>queued</code> - Message accepted and queued for delivery</li>
        <li><code>sent</code> - Message sent to device</li>
        <li><code>delivered</code> - Message delivered to recipient (if delivery reports enabled)</li>
        <li><code>failed</code> - Message delivery failed</li>
      </ol>

      <div className="not-prose bg-yellow-50 border-l-4 border-yellow-600 p-4 my-6">
        <p className="text-sm text-yellow-900">
          <strong>Note:</strong> Delivery reports depend on carrier support and device capabilities. Not all carriers provide delivery confirmations.
        </p>
      </div>
    </div>
  )
}
